import { useCallback, useMemo } from "react";
import {
  fetchClientSidebarPage,
  selectRecentClients,
  selectSidebarQuery,
  selectSidebarSection,
} from "../slices/clientSlice";
import type { TClientForm } from "../types/index.type";
import { useAppDispatch, useAppSelector } from "./redux-hooks";

type BrowseScope = "owned" | "all";

export type UseClientBrowse = {
  /** The browse list (empty-query dropdown content): newest-first, same rows as the sidebar. */
  browseClients: TClientForm[];
  browseLoading: boolean;
  browseHasMore: boolean;
  browseError: boolean;
  /** Fetch the first page if the shared list was never loaded — call on dropdown open. */
  ensureLoaded: () => void;
  /** Advance the shared cursor(s); the left-panel sidebar extends in lockstep. */
  loadMore: () => void;
  retryBrowse: () => void;
};

/**
 * The pickers' browse list, backed by THE sidebar's redux sections (`sidebar.owned` /
 * `sidebar.shared`) — one source of truth, no per-picker copy. Scrolling a picker dropdown
 * paginates the very same sections the left panel renders, so both stay in lockstep (and a page
 * loaded by the sidebar is already in the picker, and vice versa).
 *
 * ``scope="owned"`` mirrors the panel's "All Clients" tab; ``scope="all"`` appends the "Shared"
 * tab's rows after it (the pre-pagination `[...clients, ...sharedClients]` concat — flat, no
 * group headers). Because the source IS the sidebar's view, an active sidebar search filters the
 * browse list too — that is the intended "duplicate of the left panel" semantics, and `loadMore`
 * continues with the sidebar's query so pages always extend the list they belong to.
 *
 * On the demo path the sections are never fetched; the demo's stub clients (seeded via
 * `setRecentClients`) are served instead and fetching is disabled.
 */
export const useClientBrowse = (scope: BrowseScope = "owned"): UseClientBrowse => {
  const dispatch = useAppDispatch();
  const { isDemoPath } = useAppSelector((state) => state.appState);
  const owned = useAppSelector(selectSidebarSection("owned"));
  const shared = useAppSelector(selectSidebarSection("shared"));
  const byId = useAppSelector((state) => state.client.byId);
  const sidebarQuery = useAppSelector(selectSidebarQuery);
  const demoClients = useAppSelector(selectRecentClients);

  const includeShared = scope === "all";

  const browseClients = useMemo(() => {
    if (isDemoPath) return demoClients;
    // Shared rows are appended as soon as they load, NOT held back until every owned page is
    // in. Gating them on `!owned.hasMore` meant a clinician with more than one page of their
    // own patients had to scroll the entire owned list before a single shared patient
    // appeared — for a busy caseload that is "never", which is exactly the reachability bug
    // the "all" scope exists to fix. The cost is that shared rows shift down as further owned
    // pages arrive; being reachable beats holding still.
    const ids = includeShared ? [...owned.ids, ...shared.ids] : owned.ids;
    return ids.map((id) => byId[id]).filter(Boolean) as TClientForm[];
  }, [isDemoPath, demoClients, includeShared, owned.ids, shared.ids, byId]);

  const browseLoading = !isDemoPath && (owned.loading || (includeShared && shared.loading));
  const browseHasMore = !isDemoPath && (owned.hasMore || (includeShared && shared.hasMore));
  const browseError = !isDemoPath && (owned.error || (includeShared && shared.error));

  // Owned pages first (the panel's default tab), then shared — matching the concat above so
  // the scroll always extends the tail of what's rendered.
  const loadMore = useCallback(() => {
    if (isDemoPath || owned.loading || (includeShared && shared.loading)) return;
    if (owned.hasMore) {
      dispatch(fetchClientSidebarPage({ scope: "owned", offset: owned.offset, search: sidebarQuery, reset: false }));
    } else if (includeShared && shared.hasMore) {
      dispatch(fetchClientSidebarPage({ scope: "shared", offset: shared.offset, search: sidebarQuery, reset: false }));
    }
  }, [isDemoPath, includeShared, owned, shared, sidebarQuery, dispatch]);

  const ensureLoaded = useCallback(() => {
    if (isDemoPath) return;
    if (owned.ids.length === 0 && !owned.loading) {
      dispatch(fetchClientSidebarPage({ scope: "owned", offset: 0, search: sidebarQuery, reset: true }));
    }
    if (includeShared && shared.ids.length === 0 && !shared.loading) {
      dispatch(fetchClientSidebarPage({ scope: "shared", offset: 0, search: sidebarQuery, reset: true }));
    }
  }, [isDemoPath, includeShared, owned, shared, sidebarQuery, dispatch]);

  const retryBrowse = useCallback(() => {
    if (isDemoPath) return;
    if (owned.error) {
      dispatch(
        fetchClientSidebarPage({
          scope: "owned",
          offset: owned.offset,
          search: sidebarQuery,
          reset: owned.ids.length === 0,
        }),
      );
    } else if (includeShared && shared.error) {
      dispatch(
        fetchClientSidebarPage({
          scope: "shared",
          offset: shared.offset,
          search: sidebarQuery,
          reset: shared.ids.length === 0,
        }),
      );
    }
  }, [isDemoPath, includeShared, owned, shared, sidebarQuery, dispatch]);

  return { browseClients, browseLoading, browseHasMore, browseError, ensureLoaded, loadMore, retryBrowse };
};

export default useClientBrowse;
